import Promise from 'bluebird';
import _ from 'underscore';
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { WebsiteImageView } from '@bingads-webui-campaign-react/add-website-images-view';
import { ROOT_FOLDER_ID, MaxAddAssetJobInParell } from '@bingads-webui-campaign-react/asset-library-model';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { validateImageSizeAsync } from '@bingads-webui-campaign/image-experience-utils';

import { useImageExperienceAdapterForWebsiteImages } from './use-image-experience-adapter';
import { VIEWS } from '../view-type';

const ManagedWebsiteImageView = ({
  imageExperienceProps,
  minSize,
  setShowBlocker,
  assetLibraryConfig,
  assetLibraryDataService,
  logger,
  maxUploadNum,
  triggerAddImagesRef,
  setDisableApplyButton,
  setShowApplyButton,
  setSelectionInfoText,
  setErrors,
  i18n,
  perfMarker,
}) => {
  const [images, setImages] = useState([]);
  const setMoreImages = useCallback((newImages) => {
    setImages(prevImages => [...prevImages, ...newImages]);
  }, []);

  const websiteImageConfig = useImageExperienceAdapterForWebsiteImages({
    imageExperienceProps,
    minSize,
    setShowBlocker,
    setImages,
    setMoreImages,
  });

  const { imageOption, isMultimediaAd, isBrandKit } = imageExperienceProps;

  const {
    enableWebsiteMultipleSelection: enableMultipleSelection,
    onImageSelected,
    onImagesSelected,
    onImageAddError,
  } = assetLibraryConfig || {};

  // single selection mode
  const onSelectImage = useCallback((index) => {
    setShowBlocker(true);

    const imageUrl = images[index].fullUrl;

    const addImagePromise = validateImageSizeAsync(imageUrl, null, isMultimediaAd, imageOption)
      .then(() => assetLibraryDataService.uploadImageByUrl({
        imageUrl,
        folderId: ROOT_FOLDER_ID,
        sourceType: constants.sourceTypes.WebsiteImported,
      }))
      .then((res) => {
        if (_.isArray(res)) {
          throw new Error(res[0] && res[0].Code);
        }
        logger.logTrace('Ad creation flow - selected website image', { imageId: res });
        return onImageSelected({ id: res }, constants.imageSources.Website);
      })
      .catch(e => onImageAddError(e));

    return addImagePromise
      .finally(() => {
        setShowBlocker(false);
      });
  }, [setShowBlocker, images, isMultimediaAd, imageOption, assetLibraryDataService, logger, onImageSelected, onImageAddError]);

  // multi selection mode
  const onSelectImageForMultiSelection = useCallback((index) => {
    const selectedImage = images[index];
    const newImage = { ...selectedImage, isSelected: !selectedImage.isSelected };
    const newArr = [...images];
    newArr[index] = newImage;
    setImages(newArr);
  }, [images]);

  useEffect(() => {
    // init the state for multiple selection
    if (enableMultipleSelection) {
      setShowApplyButton(false);
      setDisableApplyButton(true);
      setErrors([]);
      setSelectionInfoText(i18n.getString(_TL_('0 of {{limitNumber}} images selected'), { limitNumber: maxUploadNum }));
    }
  }, [enableMultipleSelection, i18n, maxUploadNum, setDisableApplyButton, setErrors, setSelectionInfoText, setShowApplyButton]);

  useEffect(() => {
    const selectedImages = _.filter(images, image => image && image.isSelected) || [];
    if (selectedImages.length > maxUploadNum) {
      setErrors([i18n.getString(_TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.'), { limitNumber: maxUploadNum })]);
      setDisableApplyButton(true);
    } else if (selectedImages.length > 0) {
      setErrors([]);
      setShowApplyButton(true);
      setDisableApplyButton(false);
    } else {
      setErrors([]);
      setShowApplyButton(false);
      setDisableApplyButton(true);
    }

    const singularImageString = _TL_('{{selectedNumber}} of {{limitNumber}} image selected');
    const pluralImagesString = _TL_('{{selectedNumber}} of {{limitNumber}} images selected');
    const stringToUse = maxUploadNum > 1 ? pluralImagesString : singularImageString;
    setSelectionInfoText(i18n.getString(stringToUse, { selectedNumber: selectedImages.length, limitNumber: maxUploadNum }));
  }, [enableMultipleSelection, i18n, images, maxUploadNum, setDisableApplyButton, setErrors, setSelectionInfoText]);

  // only for multiple selection mode
  const onApplyButtonClick = useCallback(() => {
    const selectedImages = _.filter(images, image => image && image.isSelected) || [];

    setShowBlocker(true);

    const websiteImagePerfMarker = perfMarker.createChild('websiteImagePicker');
    websiteImagePerfMarker.willInit();

    Promise.map(
      selectedImages,
      (img) => {
        const { fullUrl } = img || {};
        return validateImageSizeAsync(fullUrl, null, isMultimediaAd, imageOption, false, false, false, false, false, false, false, false, false, isBrandKit)
          .then(() => assetLibraryDataService.uploadImageByUrl({
            imageUrl: fullUrl,
            folderId: ROOT_FOLDER_ID,
            sourceType: constants.sourceTypes.WebsiteImported,
          }))
          .then((res) => {
            if (_.isArray(res)) {
              throw new Error(res[0] && res[0].Code);
            }
            return res;
          });
      },
      { concurrency: MaxAddAssetJobInParell }
    ).then((imageIds) => {
      logger.logTrace('Ad creation flow - selected website images', { imageIds });
      return onImagesSelected(imageIds, constants.imageSources.Website);
    }).catch((err) => {
      onImageAddError(err);
    }).finally(() => {
      setShowBlocker(false);
      websiteImagePerfMarker.done();
    });
  }, [assetLibraryDataService, imageOption, images, isMultimediaAd, logger, onImageAddError, onImagesSelected, perfMarker, setShowBlocker]);

  // listen to the apply button click for multiple selection
  // eslint-disable-next-line no-param-reassign
  triggerAddImagesRef.current = enableMultipleSelection ? onApplyButtonClick : undefined;

  return (
    <WebsiteImageView
      images={images}
      {...websiteImageConfig}
      onSelectImage={enableMultipleSelection ? onSelectImageForMultiSelection : onSelectImage}
      imageCellWidth={202}
      imageCellHeight={202}
    />
  );
};

ManagedWebsiteImageView.propTypes = {
  imageExperienceProps: PropTypes.shape({
    suggestion: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.shape({
        thumbnailUrl: PropTypes.string,
        fullUrl: PropTypes.string,
      })),
      inProgess: PropTypes.bool,
      hasMore: PropTypes.bool,
    }),
    finalUrl: PropTypes.string,
    onUpdateFinalUrl: PropTypes.func,
    finalUrlProtocol: PropTypes.string,
    onUpdateFinalUrlPrefix: PropTypes.func,
    suggestionPoller: PropTypes.shape({
      getMoreLandingPageSuggestions: PropTypes.func,
      getLandingPageSuggestions: PropTypes.func,
    }),
  }).isRequired,
  minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  setShowBlocker: PropTypes.func,
  assetLibraryDataService: PropTypes.shape({}).isRequired,
  assetLibraryConfig: PropTypes.shape({
    onImageSelected: PropTypes.func,
    onImagesSelected: PropTypes.func,
    onImageAddError: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  triggerAddImagesRef: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  setShowApplyButton: PropTypes.func,
  setDisableApplyButton: PropTypes.func,
  setSelectionInfoText: PropTypes.func,
  setErrors: PropTypes.func,
  maxUploadNum: PropTypes.number,
};

ManagedWebsiteImageView.defaultProps = {
  setShowBlocker: _.noop,
  setShowApplyButton: _.noop,
  setDisableApplyButton: _.noop,
  setSelectionInfoText: _.noop,
  setErrors: _.noop,
  maxUploadNum: 16,
};

export const websiteImageSourceConfig = {
  title: _TL_('Website images'),
  showBackButton: true,
  onRenderContent: props => (
    <ManagedWebsiteImageView
      {...props}
    />
  ),
  buttonConfig: {
    label: _TL_('Website images'),
    view: VIEWS.WebsiteImage,
    iconName: 'Link12',
    buttonLocator: 'website-images',
  },
};
